import {getTestProps} from '@benchling/js-test-utils';
import React, {useState} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import HelpButton from 'help/HelpButton';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {getColumnOptionDatas} from 'insights/analytics-visualizations/configs/config-components/ConfigEditorUtils';
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {VisualizationConfigEditorComponentProps} from 'insights/analytics-visualizations/configs/VisualizationConfigEditorTypes';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

type ScatterPlotParameters = ExtractByTypename<
  AnalyticsVisualization['parameters'],
  'AnalyticsVisualizationScatterPlotParametersNode'
>;

export type rowIdColumnNameParameter = Pick<ScatterPlotParameters, 'rowIdColumnName'>;

export default function MaskConfigEditor({
  columns,
  onUpdateParameters,
  parameters,
  useSystemNames,
  isDisabled,
}: VisualizationConfigEditorComponentProps<rowIdColumnNameParameter>): JSX.Element {
  const [isMaskingEnabled, setIsMaskingEnabled] = useState(parameters.rowIdColumnName != null);

  function handleSelectIdColumnName(value: string): void {
    onUpdateParameters({rowIdColumnName: value});
  }

  function handleToggleMasking(): void {
    const newIsMaskingEnabled = !isMaskingEnabled;
    setIsMaskingEnabled(newIsMaskingEnabled);
    if (!newIsMaskingEnabled) {
      onUpdateParameters({rowIdColumnName: null});
    } else {
      onUpdateParameters({
        rowIdColumnName: useSystemNames ? columns[0].systemName : columns[0].displayName,
      });
    }
  }

  return (
    <div {...getTestProps({component: 'MaskConfigEditor', element: 'root'})}>
      <div className="u-input-spacing-top">
        <CheckboxInput
          id="allow-point-masking"
          label="Allow point masking"
          onChange={handleToggleMasking}
          checked={isMaskingEnabled}
          disabled={isDisabled}
        />
      </div>
      <div className="u-spacing-left">
        {isMaskingEnabled && (
          <div className="u-input-spacing-top">
            <FieldLabel label="Unique ID Field" />
            <HelpButton
              renderContent={() => (
                <div className="u-normal-font-weight">
                  Masked points will be tied to the unique ID field you select
                </div>
              )}
              placement={FloatingPlacement.TOP}
              iconClassName="u-mini-spacing-left"
              interaction={FloatingInteraction.HOVER}
            />
            <DropdownSelector
              allowNull={false}
              onChange={handleSelectIdColumnName}
              searchable={true}
              selectedValue={parameters.rowIdColumnName}
              optionDatas={getColumnOptionDatas(columns, useSystemNames)}
              disabled={isDisabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}
